/**
 * `usePagedList` — the client half of the list-pagination convention
 * (docs/design/LIST-PAGINATION-AUDIT.md). Turns a paginated `Operation` into a
 * URL-driven SWR query and hands back the page rows plus a ready-to-spread
 * `<TablePager>` prop bag. A paginated list consumer becomes ~3 lines:
 *
 *   const { rows, pager, isLoading } = usePagedList(commitments.list, {
 *     query: { kind }, defaultPageSize: 25, pageParam: "c_page",
 *   });
 *   return (<><Table rows={rows}/><TablePager {...pager} pageParam="c_page" /></>);
 *
 * It reads `page`/`page_size` from the URL (configurable param names so several
 * lists can share one page), merges any extra filters/sort from `query`, and
 * calls `useApi`. The `pager` bag matches `TablePagerProps`' data fields exactly;
 * pass the same `pageParam`/`pageSizeParam` to `<TablePager>` so its links write
 * back to the same params this hook reads.
 */

import * as React from "react";
import { useSearch as useSearchParams } from "../router";
import type { SWRConfiguration } from "swr";
import { useApi } from "./client";
import type { Args, Operation } from "./types";
import {
  DEFAULT_PAGE_SIZE,
  type PaginatedResult,
  type PaginationQuery,
} from "./contracts/_common";

export interface UsePagedListOptions<TQuery> {
  /** Extra filters / sort params merged into the query (page/size come from URL). */
  query?: Omit<TQuery, "page" | "page_size">;
  /** URL param for the page number. Default `"page"`. */
  pageParam?: string;
  /** URL param for the page size. Default `"page_size"`. */
  pageSizeParam?: string;
  /** Page size when the URL omits one. Should match the route default (50). */
  defaultPageSize?: number;
  /** Pass false to skip the request (e.g. a tab not yet shown). */
  enabled?: boolean;
  swr?: SWRConfiguration;
}

/** The `<TablePager>` data fields, ready to spread. */
export interface PagerProps {
  page: number;
  page_size: number;
  total: number;
  total_pages: number;
  has_next: boolean;
  has_prev: boolean;
}

export interface UsePagedListResult<Item> {
  rows: Item[];
  total: number;
  isLoading: boolean;
  error: unknown;
  /** Spread into `<TablePager {...pager} pageParam=… />`. */
  pager: PagerProps;
  /** The raw envelope, if a consumer needs more than rows + pager. */
  data: PaginatedResult<Item> | undefined;
}

export function usePagedList<TQuery extends PaginationQuery, Item>(
  // List endpoints take no path params or body — only the (paginated) query.
  op: Operation<void, TQuery, void, PaginatedResult<Item>>,
  options?: UsePagedListOptions<TQuery>,
): UsePagedListResult<Item> {
  const search = useSearchParams();
  const pageParam = options?.pageParam ?? "page";
  const pageSizeParam = options?.pageSizeParam ?? "page_size";
  const defaultPageSize = options?.defaultPageSize ?? DEFAULT_PAGE_SIZE;
  const enabled = options?.enabled ?? true;

  const page = Math.max(1, Number(search.get(pageParam) ?? "1") || 1);
  const sizeFromUrl = Number(search.get(pageSizeParam) ?? "");
  const page_size = sizeFromUrl >= 1 ? sizeFromUrl : defaultPageSize;

  // Stabilise the extra-filters object so SWR's key only changes when a filter
  // value actually changes (not on every render's fresh object identity).
  const extraQuery = options?.query;
  const extraKey = JSON.stringify(extraQuery ?? {});
  const query = React.useMemo(
    () => ({ ...(extraQuery ?? {}), page, page_size }) as TQuery,
    // extraKey captures extraQuery's value; page/page_size are primitives.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [extraKey, page, page_size],
  );

  // The `Args` conditional type can't be evaluated against the generic TQuery,
  // so build the value (params/body are void for list endpoints) and assert it.
  const args = (enabled ? { query } : null) as Args<void, TQuery, void> | null;
  const { data, isLoading, error } = useApi<void, TQuery, void, PaginatedResult<Item>>(
    op,
    args,
    options?.swr,
  );

  const env = data as PaginatedResult<Item> | undefined;
  return {
    rows: env?.rows ?? [],
    total: env?.total ?? 0,
    isLoading,
    error,
    pager: {
      page: env?.page ?? page,
      page_size: env?.page_size ?? page_size,
      total: env?.total ?? 0,
      total_pages: env?.total_pages ?? 0,
      has_next: env?.has_next ?? false,
      has_prev: env?.has_prev ?? false,
    },
    data: env,
  };
}
